import { Upload } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { formatMoneyCents } from "@/core/utils/money";
import type { StAdjustmentPushIntent } from "./types";

// The scope line — the thing a reviewer most needs to see before money moves. A
// single-technician push names the person, because "Technician #430353700" does
// not tell anyone whose pay is about to leave.
export const describePushScope = (intent: StAdjustmentPushIntent): string => {
  if (intent.technicianIds === null)
    return "Every technician with lines pending";

  const count = intent.technicianIds.length;
  if (count === 1) {
    const id = `#${intent.technicianIds[0]}`;
    // The id rides along even when named, so the confirmation matches what the
    // reviewer would look up in ServiceTitan.
    return intent.technicianName
      ? `${intent.technicianName} (${id})`
      : `Technician ${id}`;
  }

  return `${count} selected technicians`;
};

/**
 * Last stop before adjustments leave for ServiceTitan.
 *
 * A push writes to an external payroll system and cannot be recalled, and the
 * "Push all" path can send an entire period's adjustments from one click — so the
 * dialog states the scope, the line count and the money, and makes the user say
 * yes to those specific numbers.
 *
 * The copy stays neutral: pushing is the ordinary thing this tab is for, and
 * dressing the confirmation up as a warning would train people to click through
 * it. What earns attention is the figures, not alarm.
 */
export const StAdjustmentPushConfirmationDialog = ({
  open,
  intent,
  isPushing,
  onOpenChange,
  onConfirm,
}: {
  open: boolean;
  // Null while nothing is staged — the dialog is closed in that state.
  intent: StAdjustmentPushIntent | null;
  isPushing: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}) => (
  <Dialog
    open={open && !!intent}
    onOpenChange={(nextOpen) => {
      // Not while the request is in flight: closing would strand the user with no
      // idea whether the push went out.
      if (!nextOpen && !isPushing) onOpenChange(false);
    }}
  >
    {/* Wider than the 449px default so the scope line and the figures sit on one
        row each rather than wrapping into cramped columns. */}
    <DialogContent
      className="max-w-xl"
      description="Confirm the PushPay payroll adjustment push"
      hideCloseButton={isPushing}
    >
      <DialogHeader>
        <DialogTitle>Push with PushPay?</DialogTitle>
      </DialogHeader>
      {intent && (
        <Stack gap="md" className="px-6">
          <Stack
            direction="row"
            gap="lg"
            fullWidth
            className="rounded-lg border border-stroke-tertiary bg-surface-secondary px-4 py-3.5"
          >
            <Stack gap="xs" className="min-w-0 flex-1">
              <Typography
                variant="captionW1"
                className="uppercase tracking-wide text-tertiary-text"
              >
                Scope
              </Typography>
              <Typography variant="subtitleW3">
                {describePushScope(intent)}
              </Typography>
            </Stack>
            <Stack gap="xs" className="shrink-0">
              <Typography
                variant="captionW1"
                className="uppercase tracking-wide text-tertiary-text"
              >
                Lines
              </Typography>
              <Typography variant="subtitleW3" className="tabular-nums">
                {intent.lineCount}
              </Typography>
            </Stack>
            <Stack gap="xs" className="shrink-0">
              <Typography
                variant="captionW1"
                className="uppercase tracking-wide text-tertiary-text"
              >
                Amount
              </Typography>
              <Typography variant="subtitleW3" className="tabular-nums">
                {formatMoneyCents(intent.amountCents) ?? "$0"}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="bodyW2" textColor="secondary">
            PushPay writes these adjustments as payroll for the period. You
            cannot undo them here. Lines that already landed are skipped, so a
            second push never pays twice.
          </Typography>
        </Stack>
      )}
      <DialogFooter>
        <Button
          variant="secondary"
          onClick={() => onOpenChange(false)}
          disabled={isPushing}
        >
          Cancel
        </Button>
        <Button
          leftIcon={<Upload className="h-4 w-4" />}
          loading={isPushing}
          disabled={isPushing}
          onClick={onConfirm}
        >
          {intent ? `Push ${intent.lineCount}` : "Push"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
